/* ==========================================================================
   Virsaay — About (editorial)
   - Films play only while on screen, never under reduced motion, and the hero
     film carries a real pause control.
   - The making sequence is a sticky plate driven by an IntersectionObserver,
     so it works without GSAP; GSAP only adds the scrubbed drift and zoom.
   Every section stays fully readable if this file never runs.
   ========================================================================== */

(function () {
  'use strict';

  if (window.VirsaayAbout) return;

  var reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var desktopQuery = window.matchMedia('(min-width: 990px)');

  function isReduced() {
    return reducedQuery.matches || (window.VirsaayMotion && window.VirsaayMotion.reduced === true);
  }

  function each(root, selector, fn) {
    var nodes = root.matches && root.matches(selector) ? [root] : [];
    Array.prototype.push.apply(nodes, root.querySelectorAll(selector));
    nodes.forEach(fn);
  }

  function addCleanup(el, fn) {
    (el._vabCleanup = el._vabCleanup || []).push(fn);
  }

  function runCleanup(el) {
    (el._vabCleanup || []).splice(0).forEach(function (fn) {
      try {
        fn();
      } catch (e) {}
    });
  }

  function withGsap(fn) {
    var Motion = window.VirsaayMotion;
    if (!Motion) return;
    Motion.ready(function (gsap, ScrollTrigger) {
      if (!gsap || !ScrollTrigger || Motion.reduced) return;
      fn(gsap, ScrollTrigger, Motion);
    });
  }

  /* ----------------------------------------------------------------------
     Films
     ---------------------------------------------------------------------- */

  function loadVideo(video) {
    if (video.dataset.vabLoaded === 'true') return;
    Array.prototype.forEach.call(video.querySelectorAll('source[data-src]'), function (source) {
      source.src = source.dataset.src;
    });
    video.dataset.vabLoaded = 'true';
    video.load();
  }

  function syncVideo(video) {
    var shouldPlay =
      video._vabInView === true &&
      !isReduced() &&
      video.dataset.vabUserPaused !== 'true' &&
      video.dataset.vabActive !== 'false';

    if (shouldPlay) {
      loadVideo(video);
      var attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(function () {});
    } else if (!video.paused) {
      video.pause();
    }
  }

  function initVideos(section) {
    var videos = section.querySelectorAll('video[data-vab-video]');
    if (!videos.length) return;

    Array.prototype.forEach.call(videos, function (video) {
      var onPlaying = function () {
        video.classList.add('is-playing');
      };
      video.addEventListener('playing', onPlaying);
      addCleanup(section, function () {
        video.removeEventListener('playing', onPlaying);
      });
    });

    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          entry.target._vabInView = entry.isIntersecting;
          syncVideo(entry.target);
        });
      },
      { threshold: 0.2 }
    );

    Array.prototype.forEach.call(videos, function (video) {
      observer.observe(video);
    });
    addCleanup(section, function () {
      observer.disconnect();
    });

    each(section, '[data-vab-toggle]', function (button) {
      var video = section.querySelector('video[data-vab-video]');
      if (!video) return;
      button.hidden = isReduced();

      var onClick = function () {
        var pausing = button.getAttribute('aria-pressed') !== 'true';
        button.setAttribute('aria-pressed', pausing ? 'true' : 'false');
        button.querySelector('[data-vab-toggle-label]').textContent = pausing
          ? button.dataset.labelPlay
          : button.dataset.labelPause;
        video.dataset.vabUserPaused = pausing ? 'true' : 'false';
        syncVideo(video);
      };
      button.addEventListener('click', onClick);
      addCleanup(section, function () {
        button.removeEventListener('click', onClick);
      });
    });
  }

  /* ----------------------------------------------------------------------
     01 — Hero: the film settles back as the page lifts away from it
     ---------------------------------------------------------------------- */

  function initHero(section) {
    withGsap(function (gsap, ScrollTrigger, Motion) {
      var frames = section.querySelectorAll('[data-vab-hero-media] img, [data-vab-hero-media] video');
      if (!frames.length) return;

      // The film is framed on the page, so it eases back inside its plate.
      var tween = gsap.fromTo(
        frames,
        { scale: 1.12 },
        {
          scale: 1,
          ease: 'none',
          scrollTrigger: { trigger: section, start: 'top top', end: 'bottom top', scrub: true }
        }
      );
      Motion.register(section, tween.scrollTrigger);
    });
  }

  /* ----------------------------------------------------------------------
     04 — The making
     ---------------------------------------------------------------------- */

  function initMaking(section) {
    var steps = Array.prototype.slice.call(section.querySelectorAll('[data-vab-step]'));
    var stage = section.querySelector('[data-vab-stage]');
    var frame = section.querySelector('[data-vab-frame]');
    if (steps.length < 2 || !stage || !frame) return;

    var count = section.querySelector('[data-vab-count]');
    var current = section.querySelector('[data-vab-current]');
    var sequenced = null;

    function pad(n) {
      return n < 10 ? '0' + n : String(n);
    }

    function figures() {
      return steps.map(function (step) {
        return step.querySelector('[data-vab-step-media]') || frame.querySelector('[data-vab-for="' + step.dataset.vabStep + '"]');
      });
    }

    function setActive(index) {
      figures().forEach(function (figure, i) {
        if (!figure) return;
        figure.classList.toggle('is-active', i === index);
        figure.classList.toggle('is-past', i < index);
        var video = figure.querySelector('video[data-vab-video]');
        if (video) {
          video.dataset.vabActive = i === index ? 'true' : 'false';
          syncVideo(video);
        }
      });
      steps.forEach(function (step, i) {
        step.classList.toggle('is-active', i === index);
      });
      if (count) count.textContent = pad(index + 1);
      if (current) current.textContent = steps[index].dataset.vabTitle || '';
      section.style.setProperty('--vab-progress', String((index + 1) / steps.length));
    }

    function enable() {
      figures().forEach(function (figure, i) {
        if (!figure) return;
        figure.setAttribute('data-vab-for', steps[i].dataset.vabStep);
        frame.appendChild(figure);
      });
      stage.hidden = false;
      section.classList.add('is-sequenced');

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) setActive(steps.indexOf(entry.target));
          });
        },
        { rootMargin: '-48% 0px -48% 0px', threshold: 0 }
      );
      steps.forEach(function (step) {
        observer.observe(step);
      });
      setActive(0);

      sequenced = function () {
        observer.disconnect();
        section.classList.remove('is-sequenced');
        stage.hidden = true;
        figures().forEach(function (figure, i) {
          if (!figure) return;
          figure.classList.remove('is-active', 'is-past');
          var video = figure.querySelector('video[data-vab-video]');
          if (video) {
            delete video.dataset.vabActive;
            syncVideo(video);
          }
          var slot = steps[i].querySelector('[data-vab-slot]');
          if (slot) slot.appendChild(figure);
        });
        steps.forEach(function (step) {
          step.classList.remove('is-active');
        });
        if (window.VirsaayMotion) window.VirsaayMotion.refresh(60);
      };
      if (window.VirsaayMotion) window.VirsaayMotion.refresh(60);
    }

    function evaluate() {
      var want = desktopQuery.matches && 'IntersectionObserver' in window;
      if (want && !sequenced) enable();
      if (!want && sequenced) {
        sequenced();
        sequenced = null;
      }
    }

    evaluate();
    var onChange = function () {
      evaluate();
    };
    if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', onChange);
    else desktopQuery.addListener(onChange);

    addCleanup(section, function () {
      if (desktopQuery.removeEventListener) desktopQuery.removeEventListener('change', onChange);
      else desktopQuery.removeListener(onChange);
      if (sequenced) {
        sequenced();
        sequenced = null;
      }
    });
  }

  /* ----------------------------------------------------------------------
     05 — The belief: lines drift against each other like a pulled proof
     ---------------------------------------------------------------------- */

  function initBelief(section) {
    withGsap(function (gsap, ScrollTrigger, Motion) {
      var lines = section.querySelectorAll('[data-vab-drift]');
      Array.prototype.forEach.call(lines, function (line) {
        var direction = parseFloat(line.dataset.vabDrift) || 1;
        var tween = gsap.fromTo(
          line,
          { xPercent: -5 * direction },
          {
            xPercent: 5 * direction,
            ease: 'none',
            scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: true }
          }
        );
        Motion.register(section, tween.scrollTrigger);
      });

      var figure = section.querySelector('[data-vab-belief-figure] img, [data-vab-belief-figure] video');
      if (figure) {
        var lift = gsap.fromTo(
          figure,
          { yPercent: 6, scale: 1.12 },
          {
            yPercent: -6,
            scale: 1.12,
            ease: 'none',
            scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: true }
          }
        );
        Motion.register(section, lift.scrollTrigger);
      }
    });
  }

  /* ----------------------------------------------------------------------
     07 — Closing: the last image settles into frame as it arrives
     ---------------------------------------------------------------------- */

  function initClosing(section) {
    withGsap(function (gsap, ScrollTrigger, Motion) {
      var frames = section.querySelectorAll('[data-vab-closing-media] img, [data-vab-closing-media] video');
      if (!frames.length) return;
      var tween = gsap.fromTo(
        frames,
        { scale: 1.14 },
        {
          scale: 1,
          ease: 'none',
          scrollTrigger: { trigger: section, start: 'top bottom', end: 'top top', scrub: true }
        }
      );
      Motion.register(section, tween.scrollTrigger);
    });
  }

  /* ----------------------------------------------------------------------
     Boot + theme editor lifecycle
     ---------------------------------------------------------------------- */

  function init(root) {
    each(root, '[data-vab-hero]', function (section) {
      initVideos(section);
      initHero(section);
    });
    each(root, '[data-vab-making]', function (section) {
      initMaking(section);
      initVideos(section);
    });
    each(root, '[data-vab-belief]', function (section) {
      initVideos(section);
      initBelief(section);
    });
    each(root, '[data-vab-closing]', function (section) {
      initVideos(section);
      initClosing(section);
    });
    each(root, '[data-vab-films]', initVideos);
  }

  function teardown(root) {
    each(root, '[data-vab-hero], [data-vab-making], [data-vab-belief], [data-vab-closing], [data-vab-films]', function (section) {
      runCleanup(section);
      if (window.VirsaayMotion) window.VirsaayMotion.killWithin(section);
    });
  }

  function onReducedChange() {
    if (!reducedQuery.matches) return;
    Array.prototype.forEach.call(document.querySelectorAll('video[data-vab-video]'), syncVideo);
  }

  window.VirsaayAbout = { init: init, teardown: teardown };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    teardown(event.target);
  });

  if (reducedQuery.addEventListener) reducedQuery.addEventListener('change', onReducedChange);
  else if (reducedQuery.addListener) reducedQuery.addListener(onReducedChange);
})();
